import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { BillingHistory } from "@/types/license";
import { useBillingOperations } from "@/hooks/useBillingOperations";

interface PaymentStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: BillingHistory | null;
}

export const PaymentStatusDialog = ({ open, onOpenChange, invoice }: PaymentStatusDialogProps) => {
  const [status, setStatus] = useState<'paid' | 'overdue' | 'cancelled'>('paid');
  const [paidDate, setPaidDate] = useState<Date>();
  const [paymentMethod, setPaymentMethod] = useState("");
  const [notes, setNotes] = useState("");

  const { updatePaymentStatus, isUpdatingPayment } = useBillingOperations();

  React.useEffect(() => {
    if (invoice) {
      setStatus(invoice.status as 'paid' | 'overdue' | 'cancelled');
      setPaidDate(invoice.paid_date ? new Date(invoice.paid_date) : undefined);
      setPaymentMethod(invoice.payment_method || "");
      setNotes(invoice.notes || "");
    }
  }, [invoice]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invoice) return;

    updatePaymentStatus({
      invoiceId: invoice.id,
      status,
      paidDate: status === 'paid' && paidDate ? paidDate.toISOString() : undefined,
      paymentMethod: paymentMethod || undefined,
      notes: notes || undefined,
    });

    onOpenChange(false);
  };

  const statusLabels = {
    paid: 'Pago',
    overdue: 'Vencido',
    cancelled: 'Cancelado'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Atualizar Status de Pagamento</DialogTitle>
          <DialogDescription>
            Atualize o status de pagamento da fatura
          </DialogDescription>
        </DialogHeader>

        {invoice && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Fatura</Label>
              <div className="p-3 bg-muted rounded-md">
                <p className="font-medium">
                  R$ {invoice.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-muted-foreground">
                  Vencimento: {format(new Date(invoice.due_date), 'dd/MM/yyyy', { locale: ptBR })}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(value: any) => setStatus(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Pago</SelectItem>
                  <SelectItem value="overdue">Vencido</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {status === 'paid' && (
              <>
                <div className="space-y-2">
                  <Label>Data de Pagamento</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !paidDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {paidDate ? (
                          format(paidDate, "dd/MM/yyyy", { locale: ptBR })
                        ) : (
                          "Selecionar data de pagamento"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={paidDate}
                        onSelect={setPaidDate}
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-method">Método de Pagamento</Label>
                  <Input
                    id="payment-method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    placeholder="Ex: PIX, Cartão de Crédito, Boleto..."
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observações sobre o pagamento..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isUpdatingPayment}>
                {isUpdatingPayment ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};